import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Monitor, RotateCw, Smartphone, Wand2 } from "lucide-react";
import { api, errMsg, applyTheme } from "@/lib/api";
import { PageTitle, AdminButton, useList } from "@/components/admin/kit";
import { useUnsaved, UnsavedBadge, same } from "@/lib/unsaved";
import { FONTS, THEMES, resolveSkin, loadFont, SECTION_KEYS } from "@/lib/skin";
import { ArrowUp, ArrowDown } from "lucide-react";

const SECTION_LABEL = { builder: { room: "Rooms", spec: "Specs & features" }, fitness: { room: "Suites", area: "Studio areas", spec: "Modalities & amenities" }, energy: { part: "Equipment", spec: "Info" } };
function SectionOrder({ s, set, ind }) {
  const keys = SECTION_KEYS[ind] || SECTION_KEYS.builder;
  const list = [...keys].sort((a, b) => { const o = s.menu_order || []; return (o.indexOf(a) + 1 || 99) - (o.indexOf(b) + 1 || 99); });
  const move = (i, d) => { const c = [...list]; [c[i], c[i + d]] = [c[i + d], c[i]]; set("menu_order", c); };
  return (
    <div data-testid="skin-menu-order">
      <p className="text-sm font-medium mb-1">Menu section order</p>
      <div className="space-y-1">{list.map((k, i) => (
        <div key={k} className="flex items-center gap-2 rounded-lg border bg-white px-3 h-9 text-sm" data-testid={`skin-menu-order-${k}`}>
          <span className="flex-1">{i + 1}. {SECTION_LABEL[ind]?.[k] || k}</span>
          <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="p-1 disabled:opacity-20" data-testid={`skin-menu-order-up-${k}`}><ArrowUp className="w-3.5 h-3.5" /></button>
          <button type="button" disabled={i === list.length - 1} onClick={() => move(i, 1)} className="p-1 disabled:opacity-20" data-testid={`skin-menu-order-down-${k}`}><ArrowDown className="w-3.5 h-3.5" /></button>
        </div>
      ))}</div>
    </div>
  );
}
import { useTerms } from "@/lib/industry";

const COLORS = ["primary_color", "accent_color"];
const OPTIONS = {
  corners: [["rounded", "Rounded"], ["pill", "Extra round"], ["square", "Square"]],
  panel: [["glass", "Glass"], ["solid", "Solid"], ["outline", "Outline"]],
  tone: [["dark", "Dark panels"], ["light", "Light panels"], ["color", "Custom colour"]],
  bar_style: [["brand", "Brand colour"], ["dark", "Match panels"], ["light", "White"]],
  menu_layout: [["left", "Room list on left"], ["top", "Top menus only"], ["bottom", "Room strip at bottom"]],
  pin_size: [["sm", "Small"], ["md", "Medium"], ["lg", "Large"]],
  pin_shape: [["circle", "Circle"], ["square", "Square"], ["drop", "Map pin"]],
  pin_labels: [["rooms", "Room names"], ["all", "All names"], ["none", "No names"]],
  hotspot_transition: [["fly", "Fly"], ["fade", "Fade"], ["instant", "Instant"]],
  move_speed: [["slow", "Slow"], ["normal", "Normal"], ["fast", "Fast"]],
};
const DEFAULTS = { corners: "rounded", panel: "glass", tone: "dark", bar_style: "brand", pin_size: "md", pin_shape: "circle", pin_labels: "rooms", hotspot_transition: "fly", move_speed: "normal", font: "classic" };

const IND_LABELS = { energy: { left: "Equipment list on left", bottom: "Part strip at bottom", rooms: "Part names" }, fitness: { left: "Suite list on left", bottom: "Suite strip at bottom", rooms: "Suite names" } };
export const Choice = ({ k, label, hint, s, set, ind }) => (
  <div>
    <p className="text-sm font-semibold">{label}</p>
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
    <div className="flex flex-wrap gap-2 mt-2">
      {OPTIONS[k].map(([v, l]) => <button key={v} onClick={() => set(k, v)} className={`h-9 px-3 rounded-xl text-xs border transition-colors ${(s[k] || DEFAULTS[k]) === v ? "bg-slate-900 text-white border-slate-900" : "bg-white hover:border-slate-400"}`} data-testid={`skin-${k}-${v}`}>{IND_LABELS[ind]?.[v] || l}</button>)}
    </div>
  </div>
);

export function LivePreview({ houseId, skin, t, src, note }) {
  const [device, setDevice] = useState("desktop");
  const ref = useRef(null);
  const box = useRef(null);
  const [w, setW] = useState(800);
  const send = () => ref.current?.contentWindow?.postMessage({ type: "sh-skin", skin }, window.location.origin);
  useEffect(() => { send(); });
  useEffect(() => {
    const on = (e) => { if (e.data?.type === "sh-skin-ready") send(); };
    window.addEventListener("message", on);
    const ro = new ResizeObserver(([en]) => setW(en.contentRect.width));
    if (box.current) ro.observe(box.current);
    return () => { window.removeEventListener("message", on); ro.disconnect(); };
  });
  const [land, setLand] = useState(false);
  const phone = device === "phone";
  const [fw, fh] = phone ? (land ? [844, 390] : [390, 780]) : [1280, 800];
  const scale = Math.min(1, (w - 72) / fw, phone ? 600 / fh : 1);
  return (
    <div className="lg:sticky lg:top-6" data-testid="skin-live-preview">
      <div className="flex items-center gap-2 mb-3">
        <p className="text-sm font-semibold flex-1">Live preview</p>
        {[["desktop", Monitor], ["phone", Smartphone]].map(([d, I]) => <button key={d} onClick={() => setDevice(d)} className={`h-9 px-3 rounded-xl border text-xs inline-flex items-center gap-1.5 ${device === d ? "bg-slate-900 text-white" : "bg-white"}`} data-testid={`skin-preview-${d}`}><I className="w-4 h-4" />{d === "phone" ? "Phone" : "Desktop"}</button>)}
        {phone && <button onClick={() => setLand(!land)} title="Turn the phone sideways" className={`h-9 px-3 rounded-xl border text-xs inline-flex items-center gap-1.5 ${land ? "bg-slate-900 text-white" : "bg-white"}`} data-testid="skin-preview-rotate"><RotateCw className="w-4 h-4" />{land ? "Landscape" : "Portrait"}</button>}
      </div>
      <div ref={box} className="w-full rounded-3xl bg-slate-100 border grid place-items-center overflow-hidden" style={{ height: fh * scale + (phone ? (land ? 40 : 64) : 16) + 16 }}>
        {houseId ? (
          <div className={phone ? "bg-slate-900 p-2.5 rounded-[2.6rem] shadow-2xl" : "shadow-2xl rounded-xl overflow-hidden"} data-testid="skin-preview-frame">
            <div className={phone ? `rounded-[2rem] overflow-hidden bg-black flex ${land ? "flex-row" : "flex-col"}` : ""}>
              {phone && (land
                ? <div className="w-7 bg-black flex items-center justify-center"><span className="h-20 w-4 rounded-full bg-slate-900" /></div>
                : <div className="h-7 bg-black flex items-center justify-center"><span className="w-20 h-4 rounded-full bg-slate-900" /></div>)}
              <div style={{ width: fw * scale, height: fh * scale }} className="overflow-hidden">
                <iframe ref={ref} key={`${houseId}-${device}-${land}`} title="Skin preview" src={src || `/embed/${houseId}?skin_preview=1`} style={{ width: fw, height: fh, transform: `scale(${scale})`, transformOrigin: "0 0" }} className="border-0" data-testid="skin-preview-iframe" />
              </div>
              {phone && (land
                ? <div className="w-5 bg-black flex items-center justify-center"><span className="h-24 w-1 rounded-full bg-white/60" /></div>
                : <div className="h-5 bg-black flex items-center justify-center"><span className="w-24 h-1 rounded-full bg-white/60" /></div>)}
            </div>
          </div>
        ) : <p className="text-sm text-slate-500 p-10">Add a {t.item} under {t.homes} to see a live preview.</p>}
      </div>
      <p className="text-[11px] text-slate-400 mt-2">{note || "This is your real tour. The 3D view itself only loads on your published site."}</p>
    </div>
  );
}

export default function AdminSkin() {
  const [b, setB] = useState(null);
  const [orig, setOrig] = useState(null);
  const [homes, reloadHomes] = useList("/admin/house-types");
  const [target, setTarget] = useState("");
  const [ov, setOv] = useState(null);
  const { t, sells, ind } = useTerms();
  const energy = ind === "energy";
  useEffect(() => { api.get("/admin/builder").then((r) => { setB(r.data); setOrig(r.data); }); }, []);
  const home = (homes || []).find((h) => h.id === target);
  useEffect(() => { setOv(home?.skin_override || null); }, [home]);
  const dirty = (!!b && !same(b, orig)) || (!!home && !same(ov, home.skin_override || null));
  useUnsaved(dirty);
  Object.keys(FONTS).forEach(loadFont);
  if (!b) return null;
  const account = resolveSkin(b, null);
  const s = home ? resolveSkin(b, { skin_override: ov }) : account;
  const editable = !home || !!ov;
  const set = (k, v) => {
    if (home) { setOv({ ...(ov || {}), [k]: v }); return; }
    if (COLORS.includes(k)) setB({ ...b, [k]: v });
    else setB({ ...b, skin: { ...(b.skin || {}), [k]: v } });
  };
  const applyTemplate = (key) => {
    const { name, ...t } = THEMES[key];
    if (home) { setOv({ ...(ov || {}), ...t, theme: key }); return; }
    const colors = Object.fromEntries(COLORS.map((c) => [c, t[c]]));
    const rest = Object.fromEntries(Object.entries(t).filter(([k]) => !COLORS.includes(k)));
    setB({ ...b, ...colors, skin: { ...(b.skin || {}), ...rest, theme: key } });
    toast(`${name} applied. Tweak anything below, then save.`);
  };
  const save = async () => {
    try {
      if (home) {
        await api.put(`/admin/house-types/${home.id}`, { ...home, skin_override: ov });
        reloadHomes();
        toast.success(ov ? `Custom skin saved for ${home.name}` : `${home.name} now uses your account skin`);
        return;
      }
      const { data } = await api.put("/admin/builder", b); setB(data); setOrig(data); applyTheme(data); toast.success(`Tour skin saved for all ${t.items}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const Toggle = ({ k, label, def = true }) => (
    <label className="flex items-center gap-3 py-1.5 text-sm"><input type="checkbox" className="w-4 h-4" checked={s[k] ?? def} onChange={(e) => set(k, e.target.checked)} data-testid={`skin-${k}`} />{label}</label>
  );
  return (
    <div data-testid="admin-skin-page">
      <PageTitle help="skin" title="Tour skin" sub="Pick a theme, then fine-tune fonts, colours, panels, menus and pins. The preview is your real tour and updates as you go." right={<div className="flex items-center gap-3"><UnsavedBadge show={dirty} /><AdminButton onClick={save} data-testid="skin-save"><Check className="w-4 h-4" />Save skin</AdminButton></div>} />
      <div className="flex flex-wrap items-center gap-3 mb-6 rounded-2xl bg-white border p-4" data-testid="skin-target">
        <p className="text-sm font-semibold">Editing</p>
        <select value={target} onChange={(e) => setTarget(e.target.value)} className="h-10 rounded-xl border px-3 text-sm bg-white" data-testid="skin-target-select">
          <option value="">Whole account (all {t.items})</option>
          {(homes || []).map((h) => <option key={h.id} value={h.id}>{h.name}{h.skin_override ? " (custom skin)" : ""}</option>)}
        </select>
        {home && (
          <label className="flex items-center gap-2 text-sm" data-testid="skin-override-toggle">
            <input type="checkbox" className="w-4 h-4" checked={!!ov} onChange={(e) => setOv(e.target.checked ? {} : null)} />Give this {t.item} its own skin
          </label>
        )}
        {home && !ov && <p className="text-xs text-slate-500">This {t.item} uses your account skin. Tick the box to customise it.</p>}
      </div>
      <div className="grid xl:grid-cols-[380px_1fr] gap-8 items-start">
        <div className={`rounded-3xl bg-white border p-6 space-y-6 ${editable ? "" : "opacity-50 pointer-events-none"}`}>
          <div>
            <p className="text-sm font-semibold mb-2">Themes</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(THEMES).map(([k, t]) => (
                <button key={k} onClick={() => applyTemplate(k)} className={`rounded-2xl border p-3 text-left transition-colors hover:border-slate-500 ${s.theme === k ? "ring-2 ring-slate-900" : ""}`} data-testid={`skin-theme-${k}`}>
                  <span className="flex gap-1 mb-2"><span className="w-6 h-6 rounded-full border" style={{ background: t.primary_color }} /><span className="w-6 h-6 rounded-full border" style={{ background: t.accent_color }} /><span className={`w-6 h-6 rounded-full border ${t.tone === "light" ? "bg-white" : "bg-slate-900"}`} /></span>
                  <span className="text-sm font-semibold" style={{ fontFamily: `"${FONTS[t.font][1]}"` }}>{t.name}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">Colours</p>
            <div className="grid grid-cols-2 gap-3">
              {[["primary_color", "Main"], ["accent_color", "Accent"], ["pin_color", "Pin background"], ["chat_color", "AI assistant header"]].map(([k, l]) => (
                <label key={k} className="text-xs text-slate-500">{l}<input type="color" value={s[k] || (k === "pin_color" ? "#FFFFFF" : k === "chat_color" ? (s.tone === "color" ? s.panel_color : s.primary_color) || "#0F2A47" : "#0F2A47")} onChange={(e) => set(k, e.target.value)} className="mt-1 block w-full h-10 rounded-lg border" data-testid={`skin-${k}`} /></label>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold mb-2">Fonts</p>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(FONTS).map(([k, [name, head, body]]) => (
                <button key={k} onClick={() => set("font", k)} className={`rounded-xl border px-3 py-2 text-left ${(s.font || "classic") === k ? "bg-slate-900 text-white border-slate-900" : "bg-white hover:border-slate-400"}`} data-testid={`skin-font-${k}`}>
                  <span className="block text-base" style={{ fontFamily: `"${head}"` }}>{name}</span><span className="block text-[11px] opacity-70" style={{ fontFamily: `"${body}"` }}>{head} + {body}</span>
                </button>
              ))}
            </div>
          </div>
          <Choice k="corners" label="Buttons and panels" hint="Corner style" s={s} set={set} />
          <Choice k="panel" label="Panel style" s={s} set={set} />
          <Choice k="tone" label="Panel colour" s={s} set={set} />
          {s.tone === "color" && <label className="text-xs text-slate-500 block -mt-2">Panel colour (works with glass, solid and outline; text switches to dark or light automatically)<input type="color" value={s.panel_color || "#1E3A5F"} onChange={(e) => set("panel_color", e.target.value)} className="mt-1 block w-40 h-10 rounded-lg border" data-testid="skin-panel_color" /></label>}
          <Choice k="bar_style" label="Top bar" s={s} set={set} />
          <Choice k="menu_layout" label={energy ? "Equipment menu" : ind === "fitness" ? "Suite menu" : "Room menu"} hint="Top dropdown menus always show on desktop." s={s} set={set} ind={ind} />
          <SectionOrder s={s} set={set} ind={ind} />
          <Choice k="pin_size" label="Pin size" s={s} set={set} />
          <Choice k="pin_shape" label="Pin shape" s={s} set={set} />
          <Choice k="pin_labels" label="Names next to pins" s={s} set={set} ind={ind} />
          <Choice k="hotspot_transition" label="Moving to a hotspot" s={s} set={set} />
          <Choice k="move_speed" label="Camera speed" s={s} set={set} />
          <div>
            <p className="text-sm font-semibold">Show in the tour</p>
            {sells && <Toggle k="show_price" label="Live price and Personalise button" />}
            <Toggle k="logo_pins" label={sells ? "My icon inside hotspot and floor plan pins" : "My icon inside hotspot and plan pins"} def={false} />
          </div>
          <p className="text-xs text-slate-500 flex gap-2"><Wand2 className="w-4 h-4 shrink-0" />Logo and icon are set under Branding & team. Start screen, watermark, audio guide and passwords are set per {t.item} on its setup page (step 4).</p>
        </div>
        <LivePreview houseId={target || homes?.[0]?.id} skin={s} t={t} />
      </div>
    </div>
  );
}
